import React, { useContext, useMemo, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { DateTime } from 'luxon';
import { useQuery } from '@tanstack/react-query';
import { CommonCard } from './particles';
import { Grid, Box, LinearProgress } from '@mui/material';
import { ApplicationContext } from '@particles';
import { callGetEngagedPeople, callGetTotalUsers, callGetMessages } from '@api';
import { EngagementOverTime, DashboardHeader, PieChart, EngagementByAgent } from '@organisms';

const Dashboard: React.FC<RouteComponentProps> = () => {
  const { state } = useContext(ApplicationContext);

  const { user } = state;
  const companyId = user?.companyId;

  const [start, setStart] = useState<Date>(DateTime.local().minus({ days: 6 }).startOf('day').toJSDate());
  const [end, setEnd] = useState<Date>(DateTime.local().endOf('day').toJSDate());

  const { startDate, endDate } = useMemo(() => ({
    startDate: DateTime.fromJSDate(start).toISO({ suppressMilliseconds: true, includeOffset: false }),
    endDate: DateTime.fromJSDate(end).toISO({ suppressMilliseconds: true, includeOffset: false })
  }), [start, end]);

  const totalUsersQuery = useQuery({
    queryKey: ['admin-dashboard/total-users', companyId],
    queryFn: () => callGetTotalUsers({ companyId }),
    enabled: !!companyId
  });

  const engagedPeopleQuery = useQuery({
    queryKey: ['admin-dashboard/engaged-people', companyId, startDate, endDate],
    queryFn: () => callGetEngagedPeople({ from: startDate!, to: endDate! }),
    enabled: !!companyId && !!startDate && !!endDate
  });

  const messagesQuery = useQuery({
    queryKey: ['admin-dashboard/messages', companyId, startDate, endDate],
    queryFn: () => callGetMessages({ from: startDate!, to: endDate! }),
    enabled: !!companyId && !!startDate && !!endDate
  });

  const loading = totalUsersQuery.isFetching || engagedPeopleQuery.isFetching || messagesQuery.isFetching;

  const activeInactive: ActiveInactive = totalUsersQuery.data?.code === 200 && totalUsersQuery.data.data
    ? totalUsersQuery.data.data.data.total
    : { active: 0, inactive: 0 };

  const sentReceived: SentReceived = messagesQuery.data?.code === 200 && messagesQuery.data.data
    ? messagesQuery.data.data.data
    : { sent: 0, received: 0 };

  const totalContacts = useMemo(() => {
    if (engagedPeopleQuery.data?.code !== 200 || !engagedPeopleQuery.data.data) return 0;
    const company = engagedPeopleQuery.data.data.data.find((item) => item.id === companyId);
    return company ? company.contacts : 0;
  }, [engagedPeopleQuery.data, companyId]);

  return (
    <Box sx={{ m: 2 }}>
      {loading && <LinearProgress />}
      <Box sx={{ mt: 1, mb: 1 }}>
        <DashboardHeader
          start={start}
          end={end}
          onSelectRange={(start, end) => {
            setStart(start);
            setEnd(end);
          }}
        />
      </Box>

      <Box mt={1}>
        <Grid container columnSpacing={2}>
          <Grid md={4} sm={12} item>
            <CommonCard
              totalContacts={totalContacts}
              totalMessages={sentReceived.sent + sentReceived.received}
              activeInactive={activeInactive}
            />
          </Grid>
          <Grid md={4} sm={12} item>
            <PieChart
              title={'Total Messages'}
              title1={'Received'}
              title2={'Sent'}
              value1={sentReceived.received}
              value2={sentReceived.sent}
            />
          </Grid>
          <Grid md={4} sm={12} item>
            <PieChart
              title={'Total Licences'}
              title1={'Active'}
              title2={'Inactive'}
              value1={activeInactive.active}
              value2={activeInactive.inactive}
            />
          </Grid>
          {/* <Grid item md={3} sm={12}>
            <CreatedSalesRock />
          </Grid> */}
        </Grid>

        <Box mt={2}>
          <EngagementOverTime start={start} end={end} companyId={user?.companyId} />
        </Box>
        <Box mt={2}>
          <EngagementByAgent start={start} end={end} />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
